import { scry } from "@/lib/scryClient";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import ImageCropDialog from "@/components/media/ImageCropDialog";
import { auth, db, storage, functions } from "@/lib/firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { ref as sRef, deleteObject } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { prepareImageForSave, computeDefaultArt } from "@/lib/cardImage";
import { listenBinders, addCardToBinder } from "@/data/binders";
import { useNavigate, useLocation } from "react-router-dom";

/* =========================
   Types
========================= */
type Condition = "NM" | "LP" | "MP" | "HP" | "DMG";
type Status = "sale" | "trade" | "keep";

type ExistingValues = {
  qty: number;
  condition: Condition;
  foil: boolean;
  for_sale: boolean;
  for_trade: boolean;
  price: number | null;
  notes: string;
  custom_image_url: string | null;
  custom_image_path?: string | null;
  status?: Status;
};

type Scryfallish = {
  id?: string | null;
  oracle_id?: string | null;
  name?: string | null;
  set?: string | null;
  set_name?: string | null;
  lang?: string | null;
  collector_number?: string | null;
  image_uris?: {
    normal?: string | null;
    large?: string | null;
  } | null;
  card_faces?: {
    image_uris?: {
      normal?: string | null;
      large?: string | null;
    } | null;
  }[] | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  card: Scryfallish | null;
  existing: {
    docId: string;
    values: ExistingValues;
  };
  onUpdated?: () => void;
};

/* =========================
   Helpers (pricing)
========================= */
type PriceDoc = Record<string, unknown>;
const asNum = (x: unknown): number | null => {
  const v = typeof x === "string" ? Number(x) : typeof x === "number" ? x : null;
  return Number.isFinite(v as number) ? (v as number) : null;
};
const pick = (keys: string[], obj: Record<string, unknown>) => {
  for (const k of keys) {
    const v = asNum(obj[k]);
    if (v != null) return v;
  }
  return null;
};
const pickLow = (p: PriceDoc, foil: boolean) =>
  pick(foil ? ["usd_foil_low", "foil_usd_low", "foil_low"] : ["usd_low", "low"], p) ??
  pick(foil ? ["usd_foil_low", "foil_low"] : ["usd_low", "low"], ((p as any).market || {})) ??
  pick(foil ? ["foil_low"] : ["low"], ((p as any).tcgplayer || {}));
const pickHigh = (p: PriceDoc, foil: boolean) =>
  pick(foil ? ["usd_foil_high", "foil_usd_high", "foil_high"] : ["usd_high", "high"], p) ??
  pick(foil ? ["usd_foil_high", "foil_high"] : ["usd_high", "high"], ((p as any).market || {})) ??
  pick(foil ? ["foil_high"] : ["high"], ((p as any).tcgplayer || {}));
const pickMedian = (p: PriceDoc, foil: boolean) =>
  pick(
    foil
      ? [
          "usd_foil_median",
          "foil_usd_mid",
          "usd_foil_mid",
          "usd_foil",
          "foil_usd_median",
          "foil_usd",
        ]
      : ["usd_median", "usd_mid", "usd_market", "usd", "median"],
    p as any
  ) ??
  pick(foil ? ["usd_foil_mid", "foil_mid"] : ["usd_mid", "mid"], ((p as any).market || {})) ??
  pick(foil ? ["foil_mid"] : ["mid"], ((p as any).tcgplayer || {}));
const pickTcgDefault = (p: PriceDoc, foil: boolean) =>
  pick(
    foil
      ? [
          "usd_foil_market",
          "foil_usd_market",
          "foil_market",
          "usd_foil_mid",
          "foil_usd_mid",
          "foil_mid",
        ]
      : ["usd_market", "market", "usd_mid", "mid"],
    ((p as any).tcgplayer || {}) as any
  ) ?? pickMedian(p, foil);

/* =========================
   Component
========================= */
export default function EditCollectionModal({ open, onClose, card, existing, onUpdated }: Props) {
  const isOpen = Boolean(open && existing?.docId);
  const uid = auth.currentUser?.uid ?? null;

  // ADD: navigation for opening Oracle (matches Add modal)
  const navigate = useNavigate();
  const location = useLocation();
  function openOracle() {
    const id = (activeCard?.id ?? card?.id) || null;
    if (!id) return;
    navigate(`/search/oracle/${encodeURIComponent(String(id))}`, {
      state: { backgroundLocation: location },
    });
  }

  // Printing selection
  const [variants, setVariants] = useState<Scryfallish[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [variantsErr, setVariantsErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Scryfallish | null>(null);
  const activeCard = useMemo(() => selected ?? card, [selected, card]);

  // Form state (qty/price as strings for backspace-friendly editing)
  const [qtyRaw, setQtyRaw] = useState<string>("1");
  const [condition, setCondition] = useState<Condition>("NM");
  const [foil, setFoil] = useState<boolean>(false);
  const [sale, setSale] = useState<boolean>(false);
  const [trade, setTrade] = useState<boolean>(false);
  const [priceRaw, setPriceRaw] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  // Image state
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [cropOpen, setCropOpen] = useState(false);

  // Pricing state
  const [priceDoc, setPriceDoc] = useState<PriceDoc | null>(null);
  const [priceLoading, setPriceLoading] = useState<boolean>(false);

  // ADD: derived pricing values (matches Add modal expectations)
  const median = useMemo<number | null>(() => (priceDoc ? pickMedian(priceDoc, foil) : null), [priceDoc, foil]);
  const low = useMemo<number | null>(() => (priceDoc ? pickLow(priceDoc, foil) : null), [priceDoc, foil]);
  const high = useMemo<number | null>(() => (priceDoc ? pickHigh(priceDoc, foil) : null), [priceDoc, foil]);
  const tcgDefault = useMemo<number | null>(() => (priceDoc ? pickTcgDefault(priceDoc, foil) : null), [priceDoc, foil]);

  // Live vendor prices (same as Add modal)
  const {
    tcg,
    cardkingdom,
    averageMarket,
    updatedAt: vendorUpdatedAt,
    isLoading: vendorLoading,
    error: vendorError,
    endpointConfigured,
  } = useVendorPrices(activeCard?.id || undefined);

  // Binders
  const [binders, setBinders] = useState<{ id: string; name: string }[]>([]);
  const [binderId, setBinderId] = useState<string>("");

  // UX
  const [saving, setSaving] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const [modChecking, setModChecking] = useState<boolean>(false);
  const [modErr, setModErr] = useState<string | null>(null);

  // Derived art
  const defaultArt =
    activeCard?.card_faces?.[0]?.image_uris?.normal ??
    activeCard?.image_uris?.normal ??
    activeCard?.card_faces?.[0]?.image_uris?.large ??
    activeCard?.image_uris?.large ??
    null;

  // Numeric safe values for save
  const qtyNumber = useMemo(() => {
    const n = parseInt(qtyRaw, 10);
    return Number.isFinite(n) && n >= 1 ? Math.min(Math.floor(n), 9999) : 1;
  }, [qtyRaw]);

  const priceNumber = useMemo(() => {
    const t = priceRaw.trim();
    if (t === "") return null;
    const n = parseFloat(t);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null;
  }, [priceRaw]);

  // Clear price when not for sale
  useEffect(() => {
    if (!sale) setPriceRaw("");
  }, [sale]);

  /* =========================
     Effects
  ========================= */

  // Reset from existing when opening/switching record
  useEffect(() => {
    if (!isOpen) return;
    setQtyRaw(existing?.values?.qty != null ? String(existing.values.qty) : "1");
    setCondition(existing?.values?.condition ?? "NM");
    setFoil(!!existing?.values?.foil);
    setSale(!!existing?.values?.for_sale);
    setTrade(!!existing?.values?.for_trade);
    setPriceRaw(existing?.values?.price != null ? String(existing.values.price) : "");
    setNotes(existing?.values?.notes ?? "");
    setUploadedUrl(existing?.values?.custom_image_url ?? null);
    setFile(null);
    setPreviewUrl(null);
    setUploadProgress(null);
    setSelected(null);
    setBinderId("");
    setErr(null);
    setModErr(null);
  }, [isOpen, existing?.docId]);

  // Load pricing for active printing
  useEffect(() => {
    let alive = true;
    const priceId = activeCard?.id || null;
    if (!priceId || !isOpen) {
      setPriceDoc(null);
      return;
    }
    (async () => {
      try {
        setPriceLoading(true);
        const snap = await getDoc(doc(db, "prices", String(priceId)));
        if (!alive) return;
        setPriceDoc(snap.exists() ? (snap.data() as PriceDoc) : null);
      } catch {
        if (alive) setPriceDoc(null);
      } finally {
        if (alive) setPriceLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [activeCard?.id, isOpen]);

  // Listen binders while modal open
  useEffect(() => {
    if (!isOpen || !uid) return;
    return listenBinders((b) => setBinders(b.map(({ id, name }) => ({ id, name }))), uid);
  }, [isOpen, uid]);

  // File preview lifecycle
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Load alternative printings from Scryfall
  useEffect(() => {
    if (!isOpen) return;
    setVariantsErr(null);
    setVariants([]);
    setSelected(null);

    const ac = new AbortController();

    async function getOracleIdViaBackend(id: string): Promise<string | null> {
      try {
        const res = await (scry as any).getById?.(id, ac.signal);
        const json =
          typeof (res as any)?.json === "function"
            ? await (res as any).json()
            : ((res as any)?.data ?? res ?? null);
        return json?.oracle_id ?? null;
      } catch {
        return null;
      }
    }

    async function loadVariantsBackend() {
      try {
        setVariantsLoading(true);

        let oracle = activeCard?.oracle_id || card?.oracle_id || null;
        const baseId = (activeCard?.id || card?.id) ?? null;
        const baseName = (activeCard?.name || card?.name) ?? null;

        // If no oracle_id is present, fetch the base card via backend to derive it
        if (!oracle && baseId) {
          oracle = await getOracleIdViaBackend(String(baseId));
        }

        let printsData: any[] = [];
        if (oracle && (scry as any).printsByOracle) {
          const list = await (scry as any).printsByOracle(oracle, ac.signal);
          printsData = Array.isArray((list as any)?.data) ? (list as any).data : [];
        } else if (baseName) {
          // Exact-name search to get a base card, then fetch prints by oracle or id
          const named = await scry.search(`!"${String(baseName).replace(/"/g, '\\"')}"`, { unique: "prints", order: "released" }, ac.signal);
          const base = Array.isArray((named as any)?.data) ? (named as any).data[0] : null;
          if (!base) {
            setVariants([]);
            return;
          }
          if (base?.oracle_id && (scry as any).printsByOracle) {
            const list = await (scry as any).printsByOracle(base.oracle_id, ac.signal);
            printsData = Array.isArray((list as any)?.data) ? (list as any).data : [];
          } else if ((scry as any).printsById) {
            const list = await (scry as any).printsById(base.id, ac.signal);
            printsData = Array.isArray((list as any)?.data) ? (list as any).data : [];
          } else {
            printsData = base ? [base] : [];
          }
        } else {
          setVariants([]);
          return;
        }

        const all: Scryfallish[] = printsData.map((it: any) => ({
          id: it?.id ?? null,
          oracle_id: it?.oracle_id ?? null,
          name: it?.name ?? null,
          set: it?.set ?? null,
          set_name: it?.set_name ?? null,
          lang: it?.lang ?? null,
          collector_number: it?.collector_number ?? null,
          // Keep embedded CDN URLs exactly as provided by Scryfall data
          image_uris:
            it?.image_uris ??
            (Array.isArray(it?.card_faces) && it.card_faces[0]?.image_uris
              ? it.card_faces[0].image_uris
              : null),
          card_faces: it?.card_faces ?? null,
        }));

        const curId = activeCard?.id || card?.id || null;
        const sorted = [
          ...all.filter((v) => (v.id || "") === (curId || "")),
          ...all.filter((v) => (v.id || "") !== (curId || "")),
        ];
        setVariants(sorted);
      } catch (e: any) {
        setVariantsErr(e?.message || "Could not load printings.");
      } finally {
        setVariantsLoading(false);
      }
    }

    void loadVariantsBackend();
    return () => {
      try {
        ac.abort();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, card?.id, card?.oracle_id, card?.name]);

  /* =========================
     Image helpers
  ========================= */
  // Resize on client before moderation to keep request size small
  async function fileToBase64Resized(f: File, maxSide = 768, quality = 0.85): Promise<string> {
    const img = document.createElement("img");
    const url = URL.createObjectURL(f);
    try {
      await new Promise<void>((res, rej) => {
        img.onload = () => res();
        img.onerror = () => rej(new Error("Failed to load image"));
        img.src = url;
      });
      const { width, height } = img;
      const scale = Math.min(1, maxSide / Math.max(width, height));
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Canvas not supported");
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      return dataUrl.split(",")[1] || "";
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  async function precheckImage(toCheck: File): Promise<void> {
    setModErr(null);
    setModChecking(true);
    try {
      const base64 = await fileToBase64Resized(toCheck, 768, 0.85);
      const call = httpsCallable(functions, "moderateImage");
      const resp = (await call({
        imageBase64: base64,
        thresholds: { adult: "POSSIBLE", violence: "POSSIBLE", racy: "LIKELY" },
        context: { where: "editCollection", scryfall_id: activeCard?.id, name: activeCard?.name },
      })) as any;
      const { allowed, reasons } = resp?.data || {};
      if (!allowed) {
        const reasonText = Array.isArray(reasons) && reasons.length ? reasons.join(", ") : "Inappropriate content";
        throw new Error(`Image blocked by content protection (${reasonText}).`);
      }
    } finally {
      setModChecking(false);
    }
  }

  function clearCustomImage() {
    setFile(null);
    setPreviewUrl(null);
    setUploadedUrl(null);
    setUploadProgress(null);
  }

  /* =========================
     Handlers
  ========================= */
  const onQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (/^\d*$/.test(v)) setQtyRaw(v);
  };
  const onQtyBlur = () => {
    if (qtyRaw === "") setQtyRaw("1");
    else setQtyRaw(String(Math.max(1, Math.min(parseInt(qtyRaw, 10) || 1, 9999))));
  };

  const onPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (/^\d{0,7}(\.\d{0,2})?$/.test(v)) setPriceRaw(v);
  };
  const onPriceBlur = () => {
    const t = priceRaw.trim();
    if (t === "") return;
    const n = parseFloat(t);
    if (Number.isFinite(n)) setPriceRaw(n.toFixed(2));
    else setPriceRaw("");
  };

  /* =========================
     Save (overwrite existing)
  ========================= */
  async function onConfirm() {
    if (!auth.currentUser) {
      setErr("Please sign in.");
      return;
    }
    const uidNow = auth.currentUser.uid;

    setSaving(true);
    setErr(null);
    setModErr(null);
    try {
      if (file) {
        await precheckImage(file);
      }

      // Prepare image (handles upload or keeping existing)
      const img = await prepareImageForSave({
        uid: uidNow,
        card: {
          id: activeCard?.id ?? card?.id ?? null,
          name: (activeCard?.name as any) ?? (card?.name as any) ?? null,
          image_uris: (activeCard?.image_uris as any) ?? (card?.image_uris as any) ?? null,
          card_faces: (activeCard?.card_faces as any) ?? (card?.card_faces as any) ?? null,
        },
        file,
        uploadedUrl,
        onProgress: (p) => setUploadProgress(p),
      });

      const refUserCard = doc(db, "users", uidNow, "cards", existing.docId);
      const toCents = (n: number | null) => (typeof n === "number" ? Math.round(n * 100) / 100 : null);
      const resolvedPrice = sale ? toCents(priceNumber) : null;
      const payload: any = {
        qty: qtyNumber,
        condition,
        foil,
        for_sale: sale,
        for_trade: trade,
        status: sale ? "sale" : trade ? "trade" : "keep",
        price: resolvedPrice,
        notes,
        image: img.image ?? computeDefaultArt(activeCard || card),
        custom_image_url: img.custom_image_url ?? null,
        custom_image_path: img.custom_image_path ?? null,
        // Persist printing identity to match add-mode fields (if your schema expects them)
        scryfall_id: activeCard?.id ?? card?.id ?? null,
        oracle_id: activeCard?.oracle_id ?? card?.oracle_id ?? null,
        name: activeCard?.name ?? card?.name ?? null,
        set: activeCard?.set ?? card?.set ?? null,
        number: activeCard?.collector_number ?? card?.collector_number ?? null,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(refUserCard, payload);

      // Clean up old storage object if URL changed/cleared
      const oldPath = existing?.values?.custom_image_path ?? null;
      const oldUrl = existing?.values?.custom_image_url ?? null;
      const newUrl = img.custom_image_url ?? null;
      if (oldPath && oldUrl !== newUrl) {
        try {
          await deleteObject(sRef(storage, oldPath));
        } catch {
          // ignore
        }
      }

      if (binderId) {
        try {
          await addCardToBinder(binderId, existing.docId, uidNow);
        } catch {
          // ignore binder errors
        }
      }

      onUpdated?.();
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  }

  /* =========================
     Render
  ========================= */
  if (!isOpen) return null;
  const titleId = "edit-collection-title";

  return (
    <>
      <div
        className="fixed inset-0 z-[60] grid place-items-center p-3 sm:p-6"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="absolute inset-0 bg-black/70" onClick={onClose} />
        <div
          className="
            relative z-10 w-full max-w-[min(720px,92vw)]
            max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)]
            rounded-2xl border border-white/10 bg-gray-900/95 backdrop-blur
            shadow-2xl overflow-hidden flex flex-col
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <header className="px-4 py-3 border-b border-white/10 flex items-center justify-between shrink-0">
            <h3 id={titleId} className="text-white font-semibold truncate">
              Edit card
            </h3>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </header>

          {/* Body */}
          <div className="px-4 sm:px-5 py-4 overflow-y-auto">
            {/* Top row: art + identity + printing picker */}
            <div className="flex items-start gap-3">
              {defaultArt && (
                <div className="shrink-0">
                  <img
                    src={defaultArt}
                    alt={activeCard?.name ?? "Card"}
                    className="w-24 h-32 object-cover rounded border border-gray-700 cursor-pointer"
                    onClick={openOracle}
                  />
                  <div className="mt-2 text-[11px] text-white/60 text-center">
                    Click art to open Oracle
                  </div>
                </div>
              )}

              <div className="min-w-0 flex-1">
                <div className="text-white font-medium text-sm sm:text-base truncate">
                  {activeCard?.name ?? "Card"}
                </div>
                <div className="text-white/70 text-xs sm:text-sm truncate">
                  {(activeCard?.set || "").toUpperCase()} • #{activeCard?.collector_number ?? ""}{" "}
                  {activeCard?.lang && activeCard.lang !== "en" ? `• ${activeCard.lang.toUpperCase()}` : ""}
                </div>

                {/* Printing selector */}
               <div className="mt-2">
                  <label className="text-xs text-slate-300 block">Printing (Set)</label>
                  <div className="mt-1">
                    {variantsLoading ? (
                      <div className="text-xs text-white/60">Loading printings…</div>
                    ) : variantsErr ? (
                      <div className="text-xs text-amber-400">{variantsErr}</div>
                    ) : variants.length > 0 ? (
                      <select
                        className="w-full rounded border border-gray-700 bg-black/40 px-2 py-1 text-sm text-white"
                        value={activeCard?.id ?? ""}
                        onChange={(e) => {
                          const next = variants.find((v) => v.id === e.target.value) || null;
                          setSelected(next);
                          // keep current priceRaw as user preference; or reset:
                          // setPriceRaw(existing?.values.price != null ? String(existing.values.price) : "");
                        }}
                      >
                        {variants.map((v) => (
                          <option key={v.id ?? `${v.set}-${v.collector_number}`} value={v.id ?? ""}>
                            {(v.set || "").toUpperCase()} • #{v.collector_number ?? ""}{" "}
                            {v.set_name ? `• ${v.set_name}` : ""}
                            {v.lang && v.lang !== "en" ? ` • ${v.lang.toUpperCase()}` : ""}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="text-xs text-white/60">No alternative printings found.</div>
                    )}
                  </div>
                </div>

                {err && <div className="mt-2 text-[13px] text-amber-300">{err}</div>}
                {modChecking && <div className="mt-2 text-[13px] text-sky-300">Scanning image for safety…</div>}
                {modErr && <div className="mt-2 text-[13px] text-red-300">{modErr}</div>}

                {/* Pricing strip */}
                <div className="mt-3 rounded-md border border-white/10 bg-white/5 p-2.5">
                  <div className="flex items-center justify-between">
                    <div className="text-white/90 font-medium text-sm">
                      Market Pricing {foil ? "(Foil)" : ""}
                    </div>
                    {!endpointConfigured && (
                      <div className="text-xs text-amber-300">Endpoint not configured</div>
                    )}
                    {vendorLoading && endpointConfigured && (
                      <div className="text-xs text-white/60">Loading…</div>
                    )}
                    {vendorError && (
                      <div className="text-xs text-red-300">Error</div>
                    )}
                  </div>

                  {endpointConfigured && !vendorLoading && !vendorError && (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] text-white/80">
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">TCG Low</div>
                        <div className="font-semibold">{money(tcg?.low)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">TCG Mid</div>
                        <div className="font-semibold">{money(tcg?.mid)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">TCG Market</div>
                        <div className="font-semibold text-amber-300">
                          {money(tcg?.market)}
                        </div>
                      </div>

                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">CK Low</div>
                        <div className="font-semibold">{money(cardkingdom?.low)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">CK Mid</div>
                        <div className="font-semibold">{money(cardkingdom?.mid)}</div>
                      </div>
                      <div className="rounded bg-black/30 p-2">
                        <div className="text-white/50 text-[10px]">CK Market</div>
                        <div className="font-semibold">{money(cardkingdom?.market)}</div>
                      </div>

                      <div className="rounded bg-black/30 p-2 col-span-2 sm:col-span-3">
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-[10px]">Average Market</span>
                          <span className="font-semibold">{money(averageMarket)}</span>
                        </div>
                        <div className="mt-1 text-[10px] text-white/40">
                          {vendorUpdatedAt
                            ? `Updated ${new Date(vendorUpdatedAt).toLocaleString()}`
                            : "Updated —"}
                        </div>
                      </div>
                    </div>
                  )}

                  {!endpointConfigured && (
                    <div className="mt-2 text-xs text-white/60">
                      Add VITE_CARD_PRICES_URL to .env.local to enable live pricing.
                    </div>
                  )}

                  {vendorLoading && (
                    <div className="mt-3 h-10 rounded bg-white/10 animate-pulse" />
                  )}
                </div>
              </div>
            </div>

            {/* Form grid */}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Image picker */}
              <div className="sm:col-span-2">
                <label className="text-sm text-gray-200 block">Custom Image (optional)</label>

                {previewUrl ? (
                  // Case: new selection
                  <div className="mt-2 flex items-center gap-3">
                    <img src={previewUrl} alt="Preview" className="w-24 h-32 object-cover rounded border border-gray-700" />
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setFile(null);
                          setPreviewUrl(null);
                          setUploadProgress(null);
                        }}
                        title="Remove selected file"
                      >
                        Remove
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCropOpen(true)}
                        disabled={!previewUrl}
                        title="Crop the selected image"
                      >
                        Crop
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearCustomImage}
                        title="Revert to Scryfall art (clear custom image)"
                      >
                        Revert to Scryfall art
                      </Button>
                      {uploadProgress !== null && (
                        <div className="text-xs text-gray-300">Upload: {uploadProgress}%</div>
                      )}
                    </div>
                  </div>
                ) : uploadedUrl ? (
                  // Case: currently using a custom image
                  <div className="mt-2 flex items-center gap-3">
                    <img src={uploadedUrl} alt="Current custom" className="w-24 h-32 object-cover rounded border border-gray-700" />
                    <div className="flex flex-col gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={async (e) => {
                          const f = e.target.files?.[0] || null;
                          if (!f) return;
                          try {
                            await precheckImage(f);
                          } catch (e: any) {
                            setModErr(e?.message || "Image failed safety checks.");
                            e.target.value = "";
                            return;
                          }
                          setUploadedUrl(null);
                          setFile(f);
                        }}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        title="Choose a different image"
                      >
                        Replace image…
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearCustomImage}
                        title="Clear custom image and use Scryfall art"
                      >
                        Revert to Scryfall art
                      </Button>
                    </div>
                  </div>
                ) : (
                  // Case: no custom image → picker
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={async (e) => {
                      const f = e.target.files?.[0] || null;
                      if (!f) return;
                      try {
                        await precheckImage(f);
                      } catch (e: any) {
                        setModErr(e?.message || "Image failed safety checks.");
                        e.target.value = "";
                        return;
                      }
                      setUploadedUrl(null);
                      setFile(f);
                    }}
                    className="mt-2 block w-full text-sm text-gray-200 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-gray-800 file:text-gray-200 hover:file:bg-gray-700"
                  />
                )}

                <div className="mt-1 text-xs text-gray-400">
                  Tip: Take a clear, well‑lit photo of the full card. After selecting, use Crop to frame just the card.
                </div>
              </div>

              {/* Qty / Condition */}
              <div className="text-sm text-gray-200 flex items-center gap-2">
                <label className="text-sm text-gray-200">
                  Qty
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                    value={qtyRaw}
                    onChange={onQtyChange}
                    onBlur={onQtyBlur}
                    placeholder="1"
                  />
                </label>

                <label className="text-sm text-gray-200">
                  Condition
                  <select
                    className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value as Condition)}
                  >
                    <option value="NM">NM</option>
                    <option value="LP">LP</option>
                    <option value="MP">MP</option>
                    <option value="HP">HP</option>
                    <option value="DMG">DMG</option>
                  </select>
                </label>
              </div>

              {/* Toggles */}
              <div className="text-sm text-gray-200 flex items-center gap-2">
                <button
                  type="button"
                  aria-pressed={foil}
                  onClick={() => setFoil((v) => !v)}
                  className={[

                    "px-3 py-1 rounded border transition",
                    foil
                      ? "bg-purple-600/20 border-purple-400 text-purple-200"
                      : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                  ].join(" ")}
                  title="Toggle foil"
                >
                  Foil
                </button>
                <button
                  type="button"
                  aria-pressed={sale}
                  onClick={() => setSale((v) => !v)}
                  className={[
                    "px-3 py-1 rounded border transition",
                    sale
                      ? "bg-emerald-600/20 border-emerald-400 text-emerald-200"
                      : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                  ].join(" ")}
                  title="List for sale"
                >
                  For Sale
                </button>
                <button
                  type="button"
                  aria-pressed={trade}
                  onClick={() => setTrade((v) => !v)}
                  className={[
                    "px-3 py-1 rounded border transition",
                    trade
                      ? "bg-fuchsia-600/20 border-fuchsia-400 text-fuchsia-200"
                      : "bg-white/5 border-white/10 text-white/80 hover:bg-white/10",
                  ].join(" ")}
                  title="List for trade"
                >
                  For Trade
                </button>
              </div>

              {/* Price */}
              <label className="text-sm text-gray-200 sm:col-span-2">
                Price (if listing for sale)
                <input
                  type="text"
                  inputMode="decimal"
                  className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                  value={priceRaw}
                  onChange={onPriceChange}
                  onBlur={onPriceBlur}
                  disabled={!sale}
                  placeholder={
                    typeof tcg?.market === "number"
                      ? `Default: $${tcg.market.toFixed(2)} (TCG Market)`
                      : typeof averageMarket === "number"
                      ? `Default: $${averageMarket.toFixed(2)} (Avg)`
                      : "Enter a price or leave blank"
                  }
                />
                <div className="text-xs text-white/50 mt-1">
                  Defaults to current TCGplayer Market when available.
                </div>
              </label>

              {/* Notes */}
              <label className="text-sm text-gray-200 sm:col-span-2">
                Notes
                <textarea
                  className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-1"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special notes (promo, misprint, signature, etc.)"
                />
              </label>

              {/* Binder selector */}
              <div className="sm:col-span-2">
                <label className="text-sm text-slate-300">Add to binder</label>
                <select
                  className="mt-1 w-full bg-gray-900 border border-slate-700 rounded px-2 py-1 text-sm text-white"
                  value={binderId}
                  onChange={(e) => setBinderId(e.target.value)}
                >
                  <option value="">None</option>
                  {binders.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <div className="mt-1 text-[11px] text-white/50">
                  Selecting a binder will ensure this card is visible there.
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="px-4 sm:px-5 py-3 border-t border-white/10 flex items-center justify-end gap-2 shrink-0">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button className="btn-pixel btn-pixel-primary" onClick={onConfirm} disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </footer>
        </div>
      </div>

      {/* Crop dialog */}
      {previewUrl ? (
        <ImageCropDialog
          open={cropOpen}
          src={previewUrl}
          aspect={3 / 4}
          onCancel={() => setCropOpen(false)}
          onApply={(blob) => {
            const newFile = new File([blob], `crop_${Date.now()}.jpg`, { type: "image/jpeg" });
            setUploadedUrl(null);
            setFile(newFile);
            setCropOpen(false);
          }}
        />
      ) : null}
    </>
  );
}


